import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  TextField,
  Link,
} from '@mui/material';
import { getToken } from '../../services/tokenStore';

const MEANING_COPY = {
  approved: 'You are electronically signing to APPROVE this document.',
  rejected: 'You are electronically signing to REJECT this document.',
  reviewed: 'You are electronically signing to record your REVIEW as complete.',
  reopened: 'You are electronically signing to REOPEN this approved document for amendment.',
};

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

const authHeaders = () => {
  const token = getToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

/**
 * 21 CFR Part 11 §11.200 electronic signature capture, via either of two
 * interchangeable methods (see electronicSignature.service.js /
 * localPdfSignature.service.js on the server):
 *
 *  - Zoho Sign (default, unchanged from before): on open, POST
 *    /api/signature/start to get an embeddable Zoho Sign URL, show it in an
 *    iframe, and wait for SignatureRedirect.jsx (loaded inside that iframe
 *    once the signer finishes) to postMessage the outcome back. "completed"
 *    calls onConfirm(signSessionId).
 *  - Local certificate: no iframe/redirect — the user enters (or first sets
 *    up) a signing PIN, which POST /api/signature/start verifies and signs
 *    with server-side synchronously, returning an already-completed
 *    signSessionId directly.
 *
 * Either way, the caller's own request to the actual workflow endpoint is
 * what triggers the server to independently verify the signature before
 * doing anything — this dialog never itself decides a signature is valid.
 *
 * @param {boolean} open
 * @param {'approved'|'rejected'|'reviewed'|'reopened'} meaning
 * @param {string} documentId - identifies what's being signed (workflow document id, approval request id, etc.)
 * @param {string} [documentLabel] - human-readable label shown on the signed statement PDF
 * @param {() => void} onClose
 * @param {(signSessionId: string) => Promise<void>} onConfirm - should throw (or let the
 *   caller's fetch reject) on failure; same contract as the old onConfirm(pin).
 * @param {string} [error] - server-provided error message to display.
 * @param {boolean} [submitting]
 */
export default function ZohoSignDialog({ open, meaning, documentId, documentLabel, onClose, onConfirm, error, submitting }) {
  const [method, setMethod] = useState('zoho');
  const [signUrl, setSignUrl] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const signSessionIdRef = useRef(null);
  const confirmedRef = useRef(false);

  const [pinStatus, setPinStatus] = useState('checking'); // 'checking' | 'no-pin' | 'has-pin'
  const [pinValue, setPinValue] = useState('');
  const [pinConfirmValue, setPinConfirmValue] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinFormError, setPinFormError] = useState('');

  const resetLocalState = () => {
    setPinStatus('checking');
    setPinValue('');
    setPinConfirmValue('');
    setPinBusy(false);
    setPinFormError('');
  };

  useEffect(() => {
    if (!open) {
      setMethod('zoho');
      setSignUrl(null);
      setStartError('');
      signSessionIdRef.current = null;
      confirmedRef.current = false;
      resetLocalState();
    }
  }, [open]);

  // Zoho path — unchanged behavior: auto-starts the ceremony as soon as the
  // dialog opens in this method.
  useEffect(() => {
    if (!open || method !== 'zoho') return undefined;

    let cancelled = false;
    setStarting(true);
    setStartError('');
    setSignUrl(null);

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/signature/start`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ documentId, meaning, documentLabel, method: 'zoho' }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success) {
          setStartError(data.message || 'Failed to start electronic signature.');
          return;
        }
        signSessionIdRef.current = data.signSessionId;
        setSignUrl(data.signUrl);
      } catch (e) {
        if (!cancelled) setStartError(e.message || 'Failed to start electronic signature.');
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, method, documentId, meaning, documentLabel]);

  useEffect(() => {
    if (!open) return undefined;

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== 'zoho-sign-redirect') return;
      if (confirmedRef.current) return;

      const { status } = event.data;
      if (status === 'completed') {
        if (!signSessionIdRef.current) return;
        confirmedRef.current = true;
        onConfirm(signSessionIdRef.current);
      } else if (status === 'declined' || status === 'later') {
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open, onConfirm, onClose]);

  // Local path — check whether the user has a signing PIN set up yet.
  useEffect(() => {
    if (!open || method !== 'local') return undefined;
    let cancelled = false;
    setPinStatus('checking');
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/signature/pin/status`, { headers: authHeaders() });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setPinStatus(res.ok && data.success && data.hasPin ? 'has-pin' : 'no-pin');
      } catch {
        if (!cancelled) setPinStatus('no-pin');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, method]);

  const handleSetPin = async () => {
    setPinFormError('');
    if (!/^\d{6,12}$/.test(pinValue)) {
      setPinFormError('PIN must be 6-12 digits.');
      return;
    }
    if (pinValue !== pinConfirmValue) {
      setPinFormError('PINs do not match.');
      return;
    }
    setPinBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/signature/pin/set`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ pin: pinValue }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setPinFormError(data.message || 'Failed to set signing PIN.');
        return;
      }
      setPinStatus('has-pin');
      setPinConfirmValue('');
      setPinValue('');
    } catch (e) {
      setPinFormError(e.message || 'Failed to set signing PIN.');
    } finally {
      setPinBusy(false);
    }
  };

  const handleLocalSign = async () => {
    setPinFormError('');
    if (!pinValue) {
      setPinFormError('Enter your signing PIN.');
      return;
    }
    setPinBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/signature/start`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ documentId, meaning, documentLabel, method: 'local', pin: pinValue }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setPinFormError(data.message || 'Failed to sign with local certificate.');
        return;
      }
      setPinValue('');
      await onConfirm(data.signSessionId);
    } catch (e) {
      setPinFormError(e.message || 'Failed to sign with local certificate.');
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Electronic Signature</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 480 }}>
        <Typography variant="body2">
          {MEANING_COPY[meaning] || 'You are electronically signing this action.'}
        </Typography>
        {(error || startError) && <Alert severity="error">{error || startError}</Alert>}

        {method === 'zoho' && (
          <>
            {starting && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!starting && signUrl && (
              <Box sx={{ flex: 1, minHeight: 440 }}>
                <iframe
                  title="Zoho Sign"
                  src={signUrl}
                  width="100%"
                  height="440"
                  style={{ border: 'none' }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              <Link component="button" type="button" onClick={() => setMethod('local')} disabled={submitting}>
                Use a local certificate instead
              </Link>
            </Typography>
          </>
        )}

        {method === 'local' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pinStatus === 'checking' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {pinStatus === 'no-pin' && (
              <>
                <Alert severity="info">
                  You don't have a signing PIN set up yet. Set one now — it re-authenticates you every time you
                  sign locally, in place of a re-entered password (this account has none) or Zoho's email
                  verification.
                </Alert>
                <TextField
                  label="New signing PIN (6-12 digits)"
                  type="password"
                  inputMode="numeric"
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
                  disabled={pinBusy}
                  fullWidth
                />
                <TextField
                  label="Confirm PIN"
                  type="password"
                  inputMode="numeric"
                  value={pinConfirmValue}
                  onChange={(e) => setPinConfirmValue(e.target.value.replace(/\D/g, ''))}
                  disabled={pinBusy}
                  fullWidth
                />
                {pinFormError && <Alert severity="error">{pinFormError}</Alert>}
                <Button variant="contained" onClick={handleSetPin} disabled={pinBusy}>
                  {pinBusy ? 'Setting PIN…' : 'Set PIN'}
                </Button>
              </>
            )}
            {pinStatus === 'has-pin' && (
              <>
                <TextField
                  label="Signing PIN"
                  type="password"
                  inputMode="numeric"
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
                  disabled={pinBusy || submitting}
                  fullWidth
                  autoFocus
                />
                {pinFormError && <Alert severity="error">{pinFormError}</Alert>}
                <Button variant="contained" onClick={handleLocalSign} disabled={pinBusy || submitting}>
                  {pinBusy ? 'Signing…' : 'Sign'}
                </Button>
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              <Link component="button" type="button" onClick={() => setMethod('zoho')} disabled={pinBusy || submitting}>
                Use Zoho Sign instead
              </Link>
            </Typography>
          </Box>
        )}

        {submitting && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">Finishing up…</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
